import type { EntityState, Slice } from "@reduxjs/toolkit";
import { createEntityAdapter, createSelector, createSlice } from "@reduxjs/toolkit";
import type { WorkspacePreferencesDto } from "@somewear/api/src/proto/workspace_preferences_proto_pb";
import { selectActiveWorkspaceId } from "@somewear/auth";
import type { WritableDraft } from "immer/dist/types/types-external";

import { emitWorkspaceChangeFromServer, emitWorkspacesReceived } from "../workspace.actions";
import type { WorkspaceState } from "../workspace.state";
import { emitWorkspacePreferencesReceived } from "./workspacePreferences.actions";
import { DEFAULT_WORKSPACE_PREFERENCES_VALUES } from "./workspacePreferences.model";

export interface IWorkspacePreferencesState extends WorkspacePreferencesDto.AsObject {}

const adapter = createEntityAdapter<IWorkspacePreferencesState, string>({
	selectId: (preferences) => preferences.workspaceId,
});

export const {
	selectAll: selectAllWorkspacePreferences,
	selectById: selectWorkspacePreferencesById,
} = adapter.getSelectors((state: WorkspaceState) => state.workspacePreferences);

export const selectActiveWorkspacePreferences = createSelector(
	[selectActiveWorkspaceId, selectAllWorkspacePreferences],
	(id, preferences) =>
		preferences.find((p) => p.workspaceId === id) ?? {
			workspaceId: id ?? "",
			...DEFAULT_WORKSPACE_PREFERENCES_VALUES,
		},
);

function updatePreferences(
	state: WritableDraft<EntityState<IWorkspacePreferencesState, string>>,
	dto: WorkspacePreferencesDto.AsObject,
): void {
	if (!dto.workspaceId) return;
	adapter.upsertOne(state, dto);
}

export const workspacePreferencesSlice: Slice = createSlice({
	name: "workspacePreferences",
	initialState: adapter.getInitialState(),
	reducers: {
		_: () => {},
	},
	extraReducers: (builder) => {
		builder.addCase(emitWorkspacesReceived, (state, action) => {
			action.payload.forEach((workspace) => {
				if (!workspace.preferences) return;
				updatePreferences(state, {
					workspaceId: workspace.id,
					...workspace.preferences,
				});
			});
		});
		builder.addCase(emitWorkspaceChangeFromServer, (state, action) => {
			if (!action.payload.preferences) return;
			updatePreferences(state, {
				workspaceId: action.payload.id,
				...action.payload.preferences,
			});
		});
		builder.addCase(emitWorkspacePreferencesReceived, (state, action) => {
			updatePreferences(state, action.payload);
		});
	},
});
